import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchArticle = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/news/${id}`);
      setArticle(data);
    } catch (error) {
      setArticle(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Caricamento...</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Articolo non trovato</div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let key = 0;

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        elements.push(<div key={key++} className="h-4" />);
      } else if (trimmed.startsWith('## ')) {
        elements.push(
          <h2 key={key++} className="text-2xl font-black text-slate-900 mt-10 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {trimmed.substring(3)}
          </h2>
        );
      } else if (trimmed.startsWith('### ')) {
        elements.push(
          <h3 key={key++} className="text-xl font-bold text-blue-900 mt-8 mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {trimmed.substring(4)}
          </h3>
        );
      } else {
        elements.push(
          <p key={key++} className="text-slate-700 leading-relaxed mb-4 text-base" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {trimmed}
          </p>
        );
      }
    }
    return elements;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={article.title}
        description={article.meta_description || article.content.substring(0, 155)}
        keywords={`${article.title}, broker forex, trading online, ForexForum`}
        ogType="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.meta_description || article.content.substring(0, 155),
          "datePublished": article.created_at,
          "publisher": {
            "@type": "Organization",
            "name": "ForexForum"
          }
        }}
      />

      {/* Header */}
      <header className="bg-blue-900 text-white py-4" data-testid="article-header">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            ForexForum
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-blue-200 hover:text-white transition-colors text-sm"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla Home
          </Link>
        </div>
      </header>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-6 py-12" data-testid="article-content">
        {/* Article Header */}
        <div className="mb-10">
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 leading-tight mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {article.title}
          </h1>
          <div className="flex items-center gap-2 text-slate-500 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <Calendar className="w-4 h-4" />
            <time dateTime={article.created_at}>{formatDate(article.created_at)}</time>
            <span className="mx-2">|</span>
            <span>ForexForum Editorial</span>
          </div>
        </div>

        {/* Featured Image */}
        {article.image_url && (
          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.image_url}
              alt={article.title}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
        )}

        {/* Article Body */}
        <div className="bg-white border border-slate-200 rounded-xl p-8 sm:p-12 shadow-sm" data-testid="article-body">
          {renderContent(article.content)}
        </div>

        {/* CTA Section */}
        <div className="mt-12 bg-blue-900 text-white rounded-xl p-8 text-center" data-testid="article-cta">
          <h2 className="text-2xl font-black mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Confronta i Broker su ForexForum
          </h2>
          <p className="text-blue-200 mb-6 max-w-2xl mx-auto" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Leggi oltre 8.000 recensioni verificate e trova il broker più adatto al tuo profilo di trading.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link
              to="/companies"
              className="bg-white text-blue-900 font-bold px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="cta-companies"
            >
              Confronta Broker
            </Link>
            <Link
              to="/"
              className="border border-blue-400 text-white font-bold px-6 py-3 rounded-lg hover:bg-blue-800 transition-colors"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="cta-home"
            >
              Torna alla Home
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-blue-900 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-xs text-blue-300">
          <p>&copy; 2024-2026 ForexForum. Tutti i diritti riservati.</p>
        </div>
      </footer>
    </div>
  );
};

export default ArticlePage;
